import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { forkJoin, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { IBaseLayer } from 'shared/interfaces';
import {Layer, LayerPermission, Permission, Project} from '../../classes';
import { LayerService, PermissionService, ProjectService } from '../../services';

const cloneDeep = require('lodash/cloneDeep');
const isEqual = require('lodash/isEqual');

type Section = 'main' | 'layers' | 'perms' | 'crowdsource' | 'search' | 'tools';

@Component({
  selector: 'geo-edit-project',
  templateUrl: 'edit-project.component.html',
  styleUrls: ['./edit-project.component.less']
})
export class EditProjectComponent implements OnInit, OnChanges {
  @Input() project: Project = new Project();
  @Input() submitBtnText: string;
  @Input() baseMaps: IBaseLayer[] = [];

  @Output() save = new EventEmitter();
  @Output() cancel = new EventEmitter();

  section: Section = 'main';
  dirty = false;
  saving = false;
  error: string;

  private originalProject: Project;
  private layerSub: Subscription;
  private permissionSub: Subscription;

  constructor(private projectService: ProjectService, private layerService: LayerService, private permissionService: PermissionService) {}

  ngOnInit() {
    if (this.project.id) {
      this.getLayers();
      this.getPermissions();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.project) {
      this.setProject(changes.project.currentValue);
    }
  }

  switchSection(name: Section) {
    this.section = name;
  }

  emitCancel() {
    if (this.project.id) {
      this.layerSub.unsubscribe();
      this.permissionSub.unsubscribe();
    }
    this.cancel.emit('');
  }

  submitForm() {
    if (!this.formIsValid()) {
      this.dirty = true;
      return;
    }
    this.saveProject();
  }

  formIsValid() {
    return this.project.extent && this.project.name && this.projectHasChanged && !this.saving && !this.error;
  }

  private setProject(project: Project) {
    this.project = cloneDeep(project);
    this.originalProject = cloneDeep(project);
  }

  private getLayers() {
    this.layerSub = this.layerService.getLayersForProject(this.project.name).subscribe((data: Layer[]) => {
      this.project.layers = data;
      this.findCrowdsource(this.project.layers);
      this.project = cloneDeep(this.project);
      this.originalProject.layers = cloneDeep(data);
    });
  }

  private findCrowdsource(layers: Layer[]) {
    layers.forEach(layer => {
      if (layer.crowdsource) {
        this.project.crowdsource_layer = layer;
        this.originalProject.crowdsource_layer = cloneDeep(layer);
      }
      if (this.project.crowdsource_layer) {
        return;
      }
      this.findCrowdsource(layer.sublayers);
    });
  }

  private getPermissions() {
    this.permissionSub = this.permissionService.getProjectPermissions(this.project.slug).subscribe(permissions => {
      this.project.subjectPermissions = permissions;
      this.originalProject.permissions = cloneDeep(permissions);

      const customPermissions = this.project.subjectPermissions.filter(permission => permission.type === 'P');

      // дозапрос прав на слои
      // TODO: получать сразу всё с бэка?
      if (customPermissions.length) {
        forkJoin(customPermissions.map(permission => this.permissionService.getLayerPermissions(permission.id))).subscribe(data => {
          customPermissions.forEach((item, idx) => {
            this.project.subjectPermissions.find(perm => perm.id === item.id).layerPermissions = data[idx];
          });
        });
      }
    });
  }

  private showError(error: string): Observable<any> {
    this.error = error;
    return throwError('Ошибка');
  }

  private saveProject() {
    // При выдаче через API к url картинки добавляется MEDIA_URL -
    // убираем его, чтобы не возникало 500 ошибки при сохранении,
    // если картинку не меняли (см. #1530)
    if (typeof this.project.preview === 'string') {
      this.project.preview = this.project.preview.replace('/media/', '');
    }

    this.saving = true;

    // Удаляем ссылки на парентов в слоях,
    // т.к. JSON.stringify сыпется на конвертации
    // закольцованной структуры
    this.decycleProjectLayers(this.project.layers);

    this.projectService
      .saveProject(this.project)
      .pipe(
        finalize(() => (this.saving = false)),
        switchMap(data => forkJoin(this.updateProjectPermissions(data), this.saveLayers(this.project, data.resource_uri))),
        switchMap(data => this.updateLayerPermissions(data[0])),
        catchError(() => this.showError('Произошла ошибка при сохранении проекта!'))
      )
      .subscribe(() => this.save.emit(''));
  }

  private decycleProjectLayers(layers: Layer[]) {
    layers.forEach(layer => {
      layer.parent = null;

      if (layer.sublayers.length) {
        this.decycleProjectLayers(layer.sublayers);
      }
    });
  }

  private saveLayers(project: Project, projectUri: string): Promise<any> {
    // Сохраняем ссылку на проект в слоях
    function addProjectLink(layers: Layer[]) {
      layers.forEach(layer => {
        layer.project = projectUri;

        if (layer.sublayers.length) {
          addProjectLink(layer.sublayers);
        }
      });
    }

    addProjectLink(project.layers);

    // Добавлям виртуальный родительский слой,
    // чтобы можно было сохранить порядок слоев верхнего уровня
    const virtualLayer = new Layer();
    virtualLayer.sublayers = virtualLayer.subLayers = project.layers;
    virtualLayer.layer_type = 'virtual';
    virtualLayer.project = projectUri;

    if (project.crowdsource_layer) {
      project.crowdsource_layer.crowdsourceAttributes.forEach((attr, i) => {
        attr.crowdsourceSettings.order = i;
        attr.crowdsourceSettings.hidden = attr.crowdsourceSettings.hidden ? attr.crowdsourceSettings.hidden : false;

        if (attr.inputType === 'checkbox' && !attr.crowdsourceSettings.defaultValue) {
          attr.crowdsourceSettings.defaultValue = false;
        }
      });
    }

    const data: any = { groups: [virtualLayer] };
    if (project.deletedLayers.length) {
      data.deleted_groups = project.deletedLayers;
    }
    return this.layerService.updateLayers(data);
  }

  private updateProjectPermissions(projectData: any) {
    this.project.subjectPermissions.forEach(item => (item.project = projectData.resource_uri));
    return this.permissionService.updateProjectPermissions(projectData.slug, {
      objects: this.project.subjectPermissions,
      deleted_objects: this.project.deletedPermissions
    });
  }

  private updateLayerPermissions(permissions: Permission[]) {
    this.project.subjectPermissions.forEach(permission => {
      if (!permission.id) {
        const createdPermission = permissions.find(item => item.name === permission.name);
        permission.layerPermissions.forEach(layerPermission => (layerPermission.project_permission = createdPermission.resource_uri));
      }
    });
    // currentLayer permissions
    let layerPermissions: LayerPermission[] = [];
    this.project.subjectPermissions.forEach(permission => {
      if (permission.type === 'P') {
        layerPermissions = layerPermissions.concat(permission.layerPermissions);
      } else {
        this.project.deletedLayerPermissions = this.project.deletedLayerPermissions.concat(permission.layerPermissions);
      }
    });
    if (layerPermissions.length || this.project.deletedLayerPermissions.length) {
      return this.permissionService.updateLayerPermissions(this.project.slug, {
        objects: layerPermissions,
        deleted_objects: this.project.deletedLayerPermissions.map(item => item.layer)
      });
    }
    return of(null);
  }

  private get projectHasChanged() {
    return !isEqual(this.originalProject, this.project);
  }
}
